/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";
import { FlexContainer, Tag, Text, theme } from "@nordcloud/gnui";
import {
  ChangeDetails,
  GroupTypes,
  ResourcesCounts,
} from "~/generated/graphql";
import { NameWithLink } from "~/components/Tables/cells/NameWithLink";
import {
  FlexColumn,
  FlexColumnSmall,
  MediumIconColumn,
  TinyColumn,
} from "~/components/Tables/styles";
import { dateFormat, TABLE_LABELS } from "~/constants";
import { ROUTES } from "~/routing/routes";
import { GROUP_TYPE_LABELS } from "~/views/resourceGroups/constants";
import { Actions } from "./Actions";

export type ResourceGroups = {
  id: string;
  name: string;
  type: GroupTypes;
  resourcesCounts?: ResourcesCounts;
  changeDetails: ChangeDetails;
};

const columnHelper = createColumnHelper<ResourceGroups>();

export function columns() {
  return [
    columnHelper.accessor((row) => row?.name, {
      header: "Resource Group Name",
      cell: ({ row }) => (
        <NameWithLink
          name={row.original.name}
          link={ROUTES.resourceGroups.generateDetails(row.original.id)}
          containerStyle={{ wordBreak: "break-word", minWidth: "10rem" }}
        />
      ),
      meta: FlexColumn,
    }),
    columnHelper.accessor("type", {
      id: "type",
      header: TABLE_LABELS.type,
      cell: ({ getValue }) => {
        const type = getValue();

        if (type === GroupTypes.Mixed) {
          return (
            <FlexContainer wrap="wrap" gap={theme.spacing.spacing01}>
              <Tag text={GROUP_TYPE_LABELS[GroupTypes.Dynamic]} />
              <Tag text={GROUP_TYPE_LABELS[GroupTypes.Manual]} />
            </FlexContainer>
          );
        }

        const label = GROUP_TYPE_LABELS[type] ?? type;
        return <Tag text={label} />;
      },
      meta: FlexColumnSmall,
    }),
    columnHelper.accessor((row) => row.resourcesCounts?.totalCount ?? 0, {
      header: "Resources",
      cell: (cell) => (
        <div>{cell.getValue() === 0 ? "-" : cell.getValue()}</div>
      ),
      meta: TinyColumn,
    }),
    columnHelper.accessor((row) => row.resourcesCounts?.onlineCount ?? 0, {
      header: "Online",
      cell: (cell) => (
        <Text color="success" mb={0}>
          {cell.getValue() === 0 ? "-" : cell.getValue()}
        </Text>
      ),
      meta: TinyColumn,
    }),
    columnHelper.accessor(
      (row) => row.resourcesCounts?.disconnectedCount ?? 0,
      {
        header: "Disconnected",
        cell: (cell) => (
          <Text color="danger" mb={0}>
            {cell.getValue() === 0 ? "-" : cell.getValue()}
          </Text>
        ),
        meta: TinyColumn,
      }
    ),
    columnHelper.accessor((row) => row?.changeDetails.createdAt, {
      header: TABLE_LABELS.createdAt,
      cell: (cell) => dayjs(cell.getValue()).format(dateFormat.dayMonthYear),
      meta: FlexColumnSmall,
    }),
    columnHelper.accessor((row) => row?.changeDetails.updatedAt, {
      header: TABLE_LABELS.updatedAt,
      cell: (cell) => dayjs(cell.getValue()).format(dateFormat.dayMonthYear),
      meta: FlexColumnSmall,
    }),
    columnHelper.accessor((row) => row?.changeDetails.updatedBy, {
      header: TABLE_LABELS.updatedBy,
      cell: (cell) => cell.getValue(),
      meta: FlexColumnSmall,
    }),
    columnHelper.accessor(() => null, {
      id: "options",
      header: () => null,
      cell: ({ row }) => {
        const resourceGroupId = row.original?.id;
        const resourceGroupName = row.original.name;
        return <Actions {...{ resourceGroupId, resourceGroupName }} />;
      },
      meta: MediumIconColumn,
    }),
  ] as never;
}
